import { DriverActivityType } from '@/modules/activities/consts'

export type ActivityLaneKey =
  | 'drive'
  | 'work'
  | 'availability'
  | 'paidRest'
  | 'rest'

/** Lane order in the chart, busiest activity first. */
export const ACTIVITY_LANE_ORDER: ActivityLaneKey[] = [
  'drive',
  'work',
  'availability',
  'paidRest',
  'rest',
]

export const ACTIVITY_TYPE_BY_LANE: Record<ActivityLaneKey, string> = {
  drive: DriverActivityType.DRIVE,
  work: DriverActivityType.WORK,
  availability: DriverActivityType.AVAILABLE,
  paidRest: DriverActivityType.PAID_REST,
  rest: DriverActivityType.REST,
}

export const laneOfActivityType = (
  activityType: string
): ActivityLaneKey | null => {
  switch (activityType) {
    case DriverActivityType.DRIVE:
      return 'drive'
    case DriverActivityType.WORK:
      return 'work'
    case DriverActivityType.AVAILABLE:
      return 'availability'
    case DriverActivityType.PAID_REST:
      return 'paidRest'
    case DriverActivityType.REST:
      return 'rest'
    default:
      return null
  }
}

export type DaySpan = {
  start: Date
  end: Date
  clippedStart: boolean
  clippedEnd: boolean
}

/**
 * The chart covers the day and nothing else, so a span reaching past midnight is
 * cut at the boundary. Returns null for a span that does not touch the day at
 * all. `clipped*` says which end was cut, so the tooltip can still show the
 * activity's real times.
 */
export const clampSpanToDay = (
  start: Date,
  end: Date,
  dayStart: Date,
  dayEnd: Date
): DaySpan | null => {
  if (
    end.getTime() <= dayStart.getTime() ||
    start.getTime() >= dayEnd.getTime()
  )
    return null

  const clippedStart = start.getTime() < dayStart.getTime()
  const clippedEnd = end.getTime() > dayEnd.getTime()

  return {
    start: clippedStart ? dayStart : start,
    end: clippedEnd ? dayEnd : end,
    clippedStart,
    clippedEnd,
  }
}

type VehicleActivity = {
  datetimeStart: string
  datetimeEnd: string
  vehicleVin?: string | null
  vehicleRegistrationNumber?: string | null
}

export type VehicleSegment = {
  id: string
  vehicleVin: string
  registrationNumber: string | null
  start: Date
  end: Date
}

/**
 * One bar per stretch the driver stayed in the same vehicle. Tacho activities
 * are contiguous, so consecutive activities sharing a VIN are merged and a real
 * gap (the driver left the vehicle) starts a new segment.
 */
export const buildVehicleSegments = (
  activities: VehicleActivity[]
): VehicleSegment[] => {
  const withVehicle = activities
    .filter(activity => !!activity.vehicleVin)
    .map(activity => ({
      vehicleVin: activity.vehicleVin!,
      registrationNumber: activity.vehicleRegistrationNumber ?? null,
      start: new Date(activity.datetimeStart),
      end: new Date(activity.datetimeEnd),
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  return withVehicle.reduce<VehicleSegment[]>((segments, activity) => {
    const previous = segments[segments.length - 1]

    if (
      previous &&
      previous.vehicleVin === activity.vehicleVin &&
      activity.start.getTime() <= previous.end.getTime()
    ) {
      previous.end = new Date(
        Math.max(previous.end.getTime(), activity.end.getTime())
      )
      previous.registrationNumber =
        previous.registrationNumber ?? activity.registrationNumber
      return segments
    }

    segments.push({
      id: `${activity.vehicleVin}-${activity.start.toISOString()}`,
      vehicleVin: activity.vehicleVin,
      registrationNumber: activity.registrationNumber,
      start: activity.start,
      end: new Date(Math.max(activity.end.getTime(), activity.start.getTime())),
    })
    return segments
  }, [])
}
